import { useEffect, useMemo } from "react";
import type { ModelCatalogEntry } from "@dosia/shared";
import { useApiQuery } from "./useApiQuery.js";
import { platformModelCatalogPath } from "./api-paths.js";

export interface PlatformModelCatalogResult {
  /** 网关现行清单；不是 live 时恒为空数组（不回落随包快照）。 */
  entries: ModelCatalogEntry[];
  /** "live" = 候选来自网关实时目录；"unavailable" = 这次没读到（网关不可达 / 未发布 / 请求失败）。 */
  source: "live" | "unavailable";
  /** 首次结果到手之前为 true。后台定时重拉不会把它翻回 true（页面不跟着闪「加载中」）。 */
  loading: boolean;
}

/** 不是 live 时的重拉间隔：网关恢复后最多这么久，候选自动出现在页面上，不用超管刷新。 */
export const CATALOG_RETRY_INTERVAL_MS = 30_000;

/**
 * 不是 live 时的空候选，**同一个引用**：每 30s 一次的后台重拉若仍失败，不能每次换一个新 `[]`
 * ——消费页有按 entries 引用重算/重灌的 memo 与 effect，换引用会周期性冲掉超管还没保存的编辑。
 * 只读约定：消费方不改它。
 */
const NO_ENTRIES: ModelCatalogEntry[] = [];

/**
 * DOSIA 超管侧模型数据的单一消费 hook——「模型清单」hub 页 + 三个平台策展页
 * （brain/tool/tier-table）全部消费它，不再各自读打包快照或拼候选逻辑。
 *
 * 服务端 `/api/platform/model-catalog` 直连网关公开 catalog，并显式回传
 * `source: "live" | "unavailable"`——本 hook 只认服务端的 `"live"`，不靠「entries 是否非空」
 * 在客户端猜。
 *
 * **不再回落随包快照**（N-95，维护者 2026-10-03 拍板：模型清单只认网关下发 ∩ 超管配置）。
 * 请求失败、服务端标 unavailable、清单为空、或响应形状不对 → `entries: []` + `source: "unavailable"`，
 * 消费页挂横幅「暂时连不上网关，模型候选会在恢复后自动出现」。为了让这句话成立，不是 live
 * 期间每 {@link CATALOG_RETRY_INTERVAL_MS} 后台静默重拉一次（`useApiQuery.revalidate`），拿到
 * live 就停。服务端只缓存 live 结果，所以网关一恢复，下一次重拉就能拿到。
 */
export function usePlatformModelCatalog(): PlatformModelCatalogResult {
  const q = useApiQuery<unknown>(platformModelCatalogPath(), []);
  const { revalidate } = q;

  const result = useMemo<PlatformModelCatalogResult>(() => {
    const loading = q.data === null && q.error === null;
    const live = q.error === null ? liveEntriesOf(q.data) : undefined;
    if (live) return { entries: live, source: "live", loading: false };
    return { entries: NO_ENTRIES, source: "unavailable", loading };
  }, [q.data, q.error]);

  const waitingForGateway = !result.loading && result.source !== "live";
  useEffect(() => {
    if (!waitingForGateway) return undefined;
    const timer = setInterval(() => {
      void revalidate();
    }, CATALOG_RETRY_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [waitingForGateway, revalidate]);

  return result;
}

/**
 * 响应体运行时 guard：`source === "live"` 且至少一行合格的清单行，才算网关现行清单。
 * 「live 且空」不收（服务端本来就把空清单标成 unavailable）：拿空清单当现状，对账会把每一条
 * 已存配置都判成已下线——最坏的一种假红。
 */
function liveEntriesOf(body: unknown): ModelCatalogEntry[] | undefined {
  if (!body || typeof body !== "object") return undefined;
  const source: unknown = Reflect.get(body, "source");
  const entries: unknown = Reflect.get(body, "entries");
  if (source !== "live" || !Array.isArray(entries)) return undefined;
  const rows = entries.filter(isCatalogRow);
  return rows.length > 0 ? rows : undefined;
}

/**
 * 逐行最小形状校验：行是对象且带非空 `modelId`（消费页按它归组、比对）。其余字段由服务端
 * `parseByteSpikeManifest` 产出，这里不重复整套契约校验。
 */
function isCatalogRow(value: unknown): value is ModelCatalogEntry {
  if (!value || typeof value !== "object") return false;
  const modelId: unknown = Reflect.get(value, "modelId");
  return typeof modelId === "string" && modelId.trim().length > 0;
}
